'use client';

import { useState } from 'react';
import Image from 'next/image';
import Container from '@/components/elements/Container';
import Block from '@/components/elements/Block';
import Heading2 from '@/components/elements/Heading2';
import { buildContentstackImageUrl } from '@/lib/utils';

const PlusIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    viewBox="0 0 512 512"
    className="pointer-events-none w-3 h-3"
    aria-hidden="true"
  >
    <path d="M289.4 222.6V0h-66.8v222.6H0v66.8h222.6V512h66.8V289.4H512v-66.8z" />
  </svg>
);

const ProductLayers = ({ block, position }) => {
  const { heading, image, layers, settings } = block;
  const items = layers ?? [];
  const [selected, setSelected] = useState(0);
  const activeLayer = items[selected] ?? null;

  return (
    <Block name="product_layers" settings={settings} position={position}>
      <Container>
        {heading && (
          <Heading2 color={settings.text_color?.color_selector || "text-white"} className="text-4xl text-center mb-8">
            {heading}
          </Heading2>
        )}
        <div className="flex flex-col lg:flex-row gap-8 items-start">

          {/* Left: mattress image + callout buttons */}
          <div className="relative w-full lg:w-2/3 self-start">
            {image?.url && (
              <Image
                src={buildContentstackImageUrl(image.url, 900)}
                alt={image.description || 'Explore mattress layers'}
                width={900}
                height={600}
                className="w-full h-auto"
                sizes="(max-width: 1024px) 100vw, 66vw"
                priority
              />
            )}
            {items.map((layer, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Show layer: ${layer.name}`}
                onClick={() => setSelected(i)}
                style={{
                  top: `${layer.y_position}%`,
                  left: `${layer.x_position}%`,
                }}
                className={`absolute w-8 h-8 rounded-full grid place-items-center z-10 cursor-pointer transition-colors ${
                  i === selected
                    ? 'bg-primary text-white'
                    : 'bg-white text-primary hover:bg-primary hover:text-white'
                }`}
              >
                <PlusIcon />
              </button>
            ))}
          </div>

          {/* Right: layer detail panel */}
          {activeLayer && (
            <article className="w-full lg:w-1/3 leading-6 mx-4">
              {activeLayer.category && (
                <h3 className="font-style-headline-5 font-semibold uppercase text-primary mb-1">
                  {activeLayer.category}
                </h3>
              )}
              {activeLayer.name && (
                <h4 className="font-style-headline-5 text-primary border-b-2 pb-4 border-white mb-3">
                  {activeLayer.name}
                </h4>
              )}
              {activeLayer.layer_image?.url && (
                <div className="relative my-4 h-[130px]">
                  <Image
                    src={activeLayer.layer_image.url}
                    alt={activeLayer.layer_image.description || activeLayer.name}
                    fill
                    sizes="(max-width: 1024px) 100vw, 33vw"
                    className="object-contain object-left"
                  />
                </div>
              )}
              <div className="font-worksans text-sm text-primary leading-relaxed">
                {activeLayer.what_it_is && (
                  <p>
                    <strong>What It Is:</strong>
                    <br />
                    {activeLayer.what_it_is}
                  </p>
                )}
                {activeLayer.how_it_helps && (
                  <p className="mt-4">
                    <strong>How It Helps You Sleep:</strong>
                    <br />
                    {activeLayer.how_it_helps}
                  </p>
                )}
              </div>
            </article>
          )}

        </div>
      </Container>
    </Block>
  );
};

export default ProductLayers;
